'use client'

import Link from 'next/link'
import { MapPin } from 'lucide-react'

export type MatchScope = 'LOCAL' | 'EVERYWHERE'

/**
 * What the API resolved for this member (`GET /user/me/match-scope`).
 *
 * `preference` is what they chose; `effective` is what the Match sections are
 * actually filtered by. The two differ only when the member's saved location
 * has no readable state — then Local cannot mean anything, so the API serves
 * Everywhere and the page asks them to set their location (#997 FR-2).
 */
export type ResolvedMatchScope = {
  preference: MatchScope
  effective: MatchScope
  memberState: string | null
  locationMissing: boolean
}

const OPTIONS: ReadonlyArray<{ value: MatchScope; label: string }> = [
  { value: 'LOCAL', label: 'Local' },
  { value: 'EVERYWHERE', label: 'Everywhere' },
]

/**
 * #997 — the deliberately simplest version agreed for launch: Local means the
 * member's own state, Everywhere means no location filter. Radius and
 * interstate/overseas tiers are #564.
 */
export function MatchScopeToggle({
  scope,
  onChange,
  pending = false,
}: {
  scope: ResolvedMatchScope
  onChange: (next: MatchScope) => void
  pending?: boolean
}) {
  const active = scope.effective

  return (
    <div
      data-testid="match-scope-toggle"
      className="flex flex-wrap items-center gap-x-3 gap-y-2"
    >
      <div
        role="radiogroup"
        aria-label="How far to look for suggestions"
        className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1"
      >
        {OPTIONS.map((option) => {
          const selected = active === option.value
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              data-testid={`match-scope-${option.value.toLowerCase()}`}
              // Local is unselectable without a state to be local to — the
              // button stays visible so the control does not change shape.
              disabled={
                pending || (option.value === 'LOCAL' && scope.locationMissing)
              }
              onClick={() => onChange(option.value)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                selected
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:hover:text-gray-300'
              }`}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {scope.locationMissing ? (
        <p
          data-testid="match-scope-location-prompt"
          className="flex items-center gap-1.5 text-sm text-gray-500"
        >
          <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Showing events everywhere.{' '}
            <Link
              href="/me"
              className="font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
            >
              Add your location
            </Link>{' '}
            to see what&apos;s near you.
          </span>
        </p>
      ) : active === 'LOCAL' ? (
        <p className="text-sm text-gray-500">
          Showing events in your state, plus everything online.
        </p>
      ) : (
        <p className="text-sm text-gray-500">Showing events everywhere.</p>
      )}
    </div>
  )
}
